import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

type ThemeMode = 'light' | 'dark' | 'system'
type ResolvedTheme = 'light' | 'dark'

interface ThemeState {
  // State
  mode: ThemeMode
  resolvedTheme: ResolvedTheme

  // Actions
  setTheme: (mode: ThemeMode) => void
  initializeTheme: () => void
}

const THEME_STORAGE_KEY = 'keynews-theme-mode'

// Get system preference
const getSystemTheme = (): ResolvedTheme => {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

// Get stored theme mode
const getStoredTheme = (): ThemeMode => {
  if (typeof window === 'undefined') return 'system'
  const stored = localStorage.getItem(THEME_STORAGE_KEY)
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored
  }
  return 'system'
}

// Resolve theme based on mode
const resolveTheme = (mode: ThemeMode): ResolvedTheme => {
  if (mode === 'system') {
    return getSystemTheme()
  }
  return mode
}

// Apply theme to document
const applyTheme = (theme: ResolvedTheme) => {
  if (typeof window === 'undefined') return

  const root = document.documentElement
  if (theme === 'dark') {
    root.classList.add('dark')
  } else {
    root.classList.remove('dark')
  }
}

export const useThemeStore = create<ThemeState>()(
  devtools(
    (set, get) => ({
      mode: 'system',
      resolvedTheme: 'light',

      setTheme: (mode: ThemeMode) => {
        const resolved = resolveTheme(mode)
        localStorage.setItem(THEME_STORAGE_KEY, mode)
        applyTheme(resolved)
        set({ mode, resolvedTheme: resolved })
      },

      initializeTheme: () => {
        const mode = getStoredTheme()
        const resolved = resolveTheme(mode)
        applyTheme(resolved)
        set({ mode, resolvedTheme: resolved })

        // Listen for system theme changes
        if (typeof window !== 'undefined') {
          const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
          const handleChange = () => {
            const currentMode = get().mode
            if (currentMode === 'system') {
              const newResolved = getSystemTheme()
              applyTheme(newResolved)
              set({ resolvedTheme: newResolved })
            }
          }

          // Modern browsers
          if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleChange)
          } else {
            // Safari < 14
            mediaQuery.addListener(handleChange)
          }
        }
      },
    }),
    { name: 'ThemeStore' }
  )
)

// Hook for easier usage
export const useTheme = () => {
  const { mode, resolvedTheme, setTheme } = useThemeStore()
  return { mode, resolvedTheme, setTheme }
}
